import { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AngularContext } from 'react-app';
import moment from 'moment';
import pusherService from 'shared/services/pusher-service';

import MentoringProgramContext from 'athena/components/mentoring-program/context';

import { css } from '@emotion/react';
import { novoEdBlack } from 'athena/styles/colors';
import { white } from 'styles/global_defaults/colors';

import { useAppDispatch } from 'redux/store';
import { getMentoringProgram } from 'redux/actions/mentoring-programs';
import { SingleMentorRequestStatus } from 'redux/schemas/models/mentor-request';
import { getMentoringProgramEnrollment } from 'redux/selectors/mentoring-program-enrollments';
import { MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import { getMentoringProgramRequestsByStatus } from 'redux/selectors/mentorship-program-connections';

import useMentoringRequests from 'athena/hooks/use-mentoring-program-requests';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';

import ConnectionInfo from './connection-info';
import NoConnection from './no-connection';
import MentoringRequestsList from './mentoring-requests-list';
import { MentoringProgramMatchingType } from 'redux/schemas/models/mentoring-programs';

const ConnectionDetails = () => {
  const { injectServices } = useContext(AngularContext);
  const [$state, CurrentUserManager] = injectServices(['$state', 'CurrentUserManager']);
  const dispatch = useAppDispatch();

  const { mentoringProgram } = useContext(MentoringProgramContext);
  const {
    id: mentorshipProgramId,
    mentorshipProgramEnrollment: enrollmentId,
    matchAnnouncementDate,
    matchingType,
  } = mentoringProgram || {};
  const { reset: resetMentoringRequests } = useMentoringRequests();
  const mentoringRequests = useSelector(
    getMentoringProgramRequestsByStatus(SingleMentorRequestStatus.PENDING, enrollmentId)
  );

  const mentorshipProgramEnrollment: MentoringProgramEnrollment = useSelector(
    (state) => getMentoringProgramEnrollment(state, enrollmentId),
  );
  const { assignedConnections } = mentorshipProgramEnrollment || {};
  const { isMentee } = useMentoringProgramRole();
  const hasRequests = mentoringRequests?.length > 0;

  const connectionId = parseInt($state.params.connectionId, 10);
  const connection = assignedConnections.find((connec) => (
    connec.connectionId === connectionId
  )) ?? assignedConnections[0];
  const { id, user, role } = connection?.assignedEnrollment || {};

  const announcementDateHasPassed = moment().isAfter(moment(matchAnnouncementDate));
  const isAutoMatching = matchingType === MentoringProgramMatchingType.AUTOMATED_MATCHING;
  const isParticipantDrivenMatching = matchingType === MentoringProgramMatchingType.PARTICIPANT_DRIVEN;
  const showConnection = id && (
    (isAutoMatching && announcementDateHasPassed)
    || isParticipantDrivenMatching
  );

  const containerClasses = 'd-flex flex-column right-panel';
  const styles = css`
    min-height: 228px;
    height: ${(id || hasRequests) ? 'fit-content' : '300px'};
    border-radius: 10px;
    background-color: ${white};
    .title {
      color: ${novoEdBlack};
    }
  `;

  const extraClasses = !hasRequests && !showConnection
    ? 'justify-content-center'
    : '';

  const MentoringRequestsWrapper = useMemo(() => (
    hasRequests && !id && isMentee
      ? () => <MentoringRequestsList requests={mentoringRequests} />
      : NoConnection
  ), [hasRequests, id, isMentee, mentoringRequests.length]);

  const setupPusherNotifications = () => {
    const identifier = CurrentUserManager.user.anonymizedIdentifier.substr(0, 10);
    pusherService.setupUserChannel(identifier);
    const channel = pusherService.currentUserChannel();
    channel.bind('connection_request_approved', () => {
      setTimeout(() => {
        resetMentoringRequests();
        dispatch(getMentoringProgram(mentorshipProgramId));
      }, 1000);
    });
    channel.bind('connection_request_rejected', () => {
      setTimeout(() => {
        resetMentoringRequests();
      }, 1000);
    });
  };

  useEffect(() => {
    setupPusherNotifications();
  }, []);

  return (
    <div css={styles} className={`${containerClasses} ${extraClasses}`}>
      {showConnection ? (
        <ConnectionInfo
          user={user}
          role={role}
          enrollmentId={id}
          mentorshipProgramId={mentorshipProgramId}
        />
      ) : (
        <MentoringRequestsWrapper />
      )}
    </div>
  );
};

export default ConnectionDetails;
